/**
 * The variant matrix's form-side fragments, shared by the new and edit pages
 * (PR #238 review M1: both pages had verbatim copies, against conventions
 * rule 15 — second usage lifts it).
 *
 * The zod fragments are *form* shapes — looser than the wire on purpose:
 * blank-named axes and stringly numerics are legal mid-authoring states, and
 * `completeAxes` / `variantsToWire` are the boundary where the wire's rules
 * apply.
 */
import { z } from "zod";
import {
  MAX_ATTRIBUTES,
  MAX_VARIANTS,
  type VariantAttribute,
} from "@flint/domain/commerce/store/variant-matrix";

/**
 * The axis builder's field: blank-named or valueless axes are legal *form*
 * state (a row mid-authoring) — they're dropped at submit by `completeAxes`,
 * where the wire requires complete ones.
 */
export const variantAttributesFormSchema = z
  .array(
    z.object({
      name: z.string().trim().max(48),
      values: z.array(z.string().trim().min(1).max(48)).max(24),
    }),
  )
  .max(MAX_ATTRIBUTES);

/** The variants table's field — stringly numerics, parsed at the wire boundary. */
export const variantsFormSchema = z
  .array(
    z.object({
      attributeValues: z.record(z.string(), z.string()),
      sku: z.string().trim().max(64),
      barcode: z.string().trim().max(32),
      weightGrams: z.string().trim(),
      // No qty: stock on hand is derived from the bundles (ADR-0061).
    }),
  )
  .max(MAX_VARIANTS);

/** Axes ready for the wire — named, with at least one value. */
export function completeAxes(axes: readonly VariantAttribute[]): VariantAttribute[] {
  return axes
    .map((a) => ({ name: a.name.trim(), values: a.values.filter((x) => x.trim().length > 0) }))
    .filter((a) => a.name.length > 0 && a.values.length > 0);
}

/**
 * A bundle composed per generated variant, reduced to what the pack model and
 * the entry overlay need (issue #235 follow-up: the builder steppers list the
 * product's variants, not bare sizes).
 *
 * - `composition` — per-size sums of the chosen variants' quantities, keyed by
 *   the Size axis value, in `sizeOrder`. This is what the library pack stores,
 *   so spec 13's "composition covers every scale label" holds by construction:
 *   the scale is minted from exactly `usedSizes`.
 * - `colors` — distinct Color values among chosen variants, in first-use
 *   order. The colour spec derives from the picks (one → single, several →
 *   mixed) instead of being declared separately and possibly disagreeing.
 * - `pieces` — total quantity across variants.
 */
export function bundleFromVariantSelection(
  variants: readonly { attributeValues: Record<string, string> }[],
  quantities: readonly number[],
  sizeAxisName: string,
  colorAxisName: string | null,
  sizeOrder: readonly string[],
): {
  composition: Record<string, number>;
  usedSizes: string[];
  colors: string[];
  pieces: number;
} {
  const perSize = new Map<string, number>();
  const colors: string[] = [];
  let pieces = 0;

  variants.forEach((v, i) => {
    const qty = quantities[i] ?? 0;
    if (qty <= 0) return;
    pieces += qty;
    const size = v.attributeValues[sizeAxisName];
    if (size) perSize.set(size, (perSize.get(size) ?? 0) + qty);
    const color = colorAxisName ? v.attributeValues[colorAxisName] : undefined;
    if (color && !colors.includes(color)) colors.push(color);
  });

  const usedSizes = sizeOrder.filter((s) => perSize.has(s));
  const composition: Record<string, number> = {};
  for (const s of usedSizes) composition[s] = perSize.get(s) ?? 0;
  return { composition, usedSizes, colors, pieces };
}
